<script>
    import { onMount, untrack } from 'svelte';
    import { randomString } from '../methods/randomString.js';

    let { options: optionsProp, data = $bindable(), translate: translateProp } = $props();
    // options と translate はコンポーネント生存中は不変。untrack で初期値に固定する。
    const options = untrack(() => optionsProp);
    const translate = untrack(() => translateProp);
    options.forEach((option) => {
        option.id = option.id || randomString();
        option.choices = toChoices(option.op);
    });

    // 選択肢（op）はコンテンツタイプ側で未入力だと null で渡ってくる。また同じ値が重複すると
    // keyed each のキー重複エラーになる。どちらもカレンダー全体の描画が止まるため、ここで正規化する。
    function toChoices(op) {
        if (op === null || op === undefined || op === '') {
            return [];
        }
        return [...new Set(String(op).split(','))].filter((choice) => choice !== '');
    }

    let setContentData = $state('');
    $effect(() => {
        setContentData = JSON.stringify({ items: data.items, year: data.year, month: data.month });
    });

    let rawDataVisibility = $state(data.meta.rawDataVisibility);

    if (Array.isArray(data.items) === false) {
        data.items = [];
    }
    const dayList = [
        translate['Sun'],
        translate['Mon'],
        translate['Tue'],
        translate['Wed'],
        translate['Thu'],
        translate['Fri'],
        translate['Sat']
    ];

    function initializeItems() {
        if (data.items.length === 0) {
            const lastDate = new Date(data.year, data.month, 0).getDate();
            for (let i = 1; i <= lastDate; i++) {
                data.items.push(createItem(i));
            }
            data = { ...data };
        }
    }

    function createItem(date) {
        const item = { date, day: getDay(date) };
        options.forEach((option) => {
            if (option.defaultValue) {
                item[option.handle] = option.defaultValue;
            } else if (option.value === 'checkbox') {
                item[option.handle] = [];
            }
        });
        return item;
    }

    function toggleRawData() {
        rawDataVisibility = !rawDataVisibility;
    }

    function changeYear() {
        let newYear = window.prompt('', data.year);
        // prompt をキャンセルすると null が返る。null.replace() で TypeError になるため早期 return する。
        if (newYear === null) return;
        newYear = newYear
            .replace(/０/g, '0')
            .replace(/１/g, '1')
            .replace(/２/g, '2')
            .replace(/３/g, '3')
            .replace(/４/g, '4')
            .replace(/５/g, '5')
            .replace(/６/g, '6')
            .replace(/７/g, '7')
            .replace(/８/g, '8')
            .replace(/９/g, '9');
        if (!/^\d{4}$/.test(newYear)) {
            alert(translate['Invalid Year']);
            return;
        }
        data.year = newYear;
        updateCalendar();
    }

    function getDay(date) {
        const dayNumber = new Date(data.year, data.month - 1, date).getDay();
        return dayList[dayNumber];
    }

    function updateCalendar() {
        const lastDate = new Date(data.year, data.month, 0).getDate();
        data.items = data.items.slice(0, lastDate).map((item, i) => ({ ...item, date: i + 1, day: getDay(i + 1) }));
        while (data.items.length < lastDate) {
            data.items.push(createItem(data.items.length + 1));
        }
        data = { ...data };
    }

    function updateData() {
        data = { ...data };
    }

    onMount(() => {
        initializeItems();
    });
</script>

<div class="mb-4">
    <div class="row row-cols-lg-auto g-3 align-items-center mb-3">
        <div class="col-12">
            <input type="number" class="form-control" placeholder={translate['Input a year']} bind:value={data.year} readonly />
        </div>
        <div class="col-12">
            <input type="button" class="btn btn-default" value={translate['Change Year']} onclick={changeYear} />
        </div>
    </div>
    <div class="row row-cols-lg-auto g-3 align-items-center mb-3">
        <div class="col-12">
            <select class="form-select" bind:value={data.month} onchange={updateCalendar}>
                <option>{translate['Select Month']}</option>
                <option value="01">{translate['January']}</option>
                <option value="02">{translate['February']}</option>
                <option value="03">{translate['March']}</option>
                <option value="04">{translate['April']}</option>
                <option value="05">{translate['May']}</option>
                <option value="06">{translate['June']}</option>
                <option value="07">{translate['July']}</option>
                <option value="08">{translate['August']}</option>
                <option value="09">{translate['September']}</option>
                <option value="10">{translate['October']}</option>
                <option value="11">{translate['November']}</option>
                <option value="12">{translate['December']}</option>
            </select>
        </div>
        <div class="col-12">
            <button type="button" class="btn btn-default" onclick={toggleRawData}>
                <svg role="img" class="mt-icon mt-icon--sm">
                    <use xlink:href={`${window.StaticURI}images/sprite.svg#ic_code`} />
                </svg>
                {rawDataVisibility ? translate['Hide Raw Data'] : translate['Show Raw Data']}
            </button>
        </div>
    </div>

    <textarea
        id={`content-field-${data.meta.contentFieldId}`}
        name={`content-field-${data.meta.contentFieldId}`}
        class="form-control text long html5-form content-field mb-3 font-monospace"
        class:d-none={!rawDataVisibility}
        rows="8"
        bind:value={setContentData}></textarea>

    <div class="mt-table--outline">
        <table class="table mt-table" style="table-layout: fixed">
            <thead>
                <tr>
                    <th scope="col" style="width: 5em">日付</th>
                    {#each options as option}
                        <th scope="col">{option.label}</th>
                    {/each}
                </tr>
            </thead>
            <tbody>
                {#each data.items as item, itemIndex (itemIndex)}
                    <tr>
                        <td style="width: 5em">
                            <span class="schedule-date">{item.date}</span>
                            {#if item.day}
                                <span class="schedule-day">({item.day})</span>
                            {/if}
                        </td>
                        {#each options as option, optionIndex (option.id)}
                            {#if option.value === 'textarea'}
                                <td><textarea class="form-control text" bind:value={item[option.handle]} onchange={updateData}></textarea></td>
                            {:else if option.value === 'select'}
                                <td>
                                    <select class="form-select" bind:value={item[option.handle]} onchange={updateData}>
                                        <option value="">-</option>
                                        {#each option.choices as opItem (opItem)}
                                            <option value={opItem}>{opItem}</option>
                                        {/each}
                                    </select>
                                </td>
                            {:else if option.value === 'checkbox'}
                                <td>
                                    {#each option.choices as opItem (`${itemIndex}-${optionIndex}-${opItem}`)}
                                        <div class="form-check">
                                            <label class="form-check-label">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    value={opItem}
                                                    bind:group={item[option.handle]}
                                                    onchange={updateData} />{opItem}
                                            </label>
                                        </div>
                                    {/each}
                                </td>
                            {:else if option.value === 'radio'}
                                <td>
                                    {#each option.choices as opItem (`${itemIndex}-${optionIndex}-${opItem}`)}
                                        <div class="form-check">
                                            <label class="form-check-label">
                                                <input
                                                    class="form-check-input"
                                                    type="radio"
                                                    value={opItem}
                                                    bind:group={item[option.handle]}
                                                    onchange={updateData} />{opItem}
                                            </label>
                                        </div>
                                    {/each}
                                </td>
                            {:else if option.value === 'number'}
                                <td><input type="number" class="form-control" bind:value={item[option.handle]} onchange={updateData} /></td>
                            {:else if option.value === 'date'}
                                <td><input type="date" class="form-control" bind:value={item[option.handle]} onchange={updateData} /></td>
                            {:else if option.value === 'time'}
                                <td><input type="time" class="form-control" bind:value={item[option.handle]} onchange={updateData} /></td>
                            {:else if option.value === 'color'}
                                <td>
                                    <input
                                        type="color"
                                        class="form-control form-control-color"
                                        bind:value={item[option.handle]}
                                        style="height: 2.4rem"
                                        onchange={updateData} />
                                </td>
                            {:else if option.value === 'email'}
                                <td><input type="email" class="form-control" bind:value={item[option.handle]} onchange={updateData} /></td>
                            {:else if option.value === 'url'}
                                <td><input type="url" class="form-control" bind:value={item[option.handle]} onchange={updateData} /></td>
                            {:else if option.value === 'tel'}
                                <td><input type="tel" class="form-control" bind:value={item[option.handle]} onchange={updateData} /></td>
                            {:else}
                                <td><input type="text" class="form-control" bind:value={item[option.handle]} onchange={updateData} /></td>
                            {/if}
                        {/each}
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>
</div>
